<template>
  <!-- Main Sidebar -->
  <div
    class="sider-primary"
    :class="{ 'sidebar-mobile-open': !isCollapsed }"
  >
    <!-- Sidebar Header -->
    <div class="sidebar-header">
      <div class="brand">
        <img src="/icons/logo-mechtrace.png" alt="MechTrace Logo"> 
        <span v-if="!isCollapsed || isMobileView">MechTrace</span>
      </div>
      
      <!-- Close button for mobile -->
      <button 
        v-if="isMobileView && !isCollapsed" 
        class="sidebar-close"
        @click="handleCollapse(true)"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M4 4L16 16M4 16L16 4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
    </div>
    
    <hr>

    <!-- Sidebar Navigation Menu -->
    <div class="sidebar-menu-wrapper">
      <a-menu 
        theme="light" 
        mode="inline"
        :selected-keys="selectedKeys"
      >
        <a-menu-item key="dashboard">
          <router-link to="/dashboard" @click="handleMenuClick">
            <span class="icon">
              <img src="/images/dashboard.png" alt="Dashboard" width="20" height="20">
            </span>
            <span class="label">Dashboard</span>
          </router-link>
        </a-menu-item>

        <a-menu-item key="air-conditioning-unit">
          <router-link to="/air-conditioning-unit" @click="handleMenuClick">
            <span class="icon">
              <img src="/images/compressor.png" alt="Machines" width="20" height="20">
            </span>
            <span class="label">Air Conditioning Unit</span>
          </router-link>
        </a-menu-item>

        <a-menu-item key="location">
          <router-link to="/location" @click="handleMenuClick">
            <span class="icon">
              <img src="/images/location.png" alt="Location" width="20" height="20">
            </span>
            <span class="label">Location</span>
          </router-link>
        </a-menu-item>

        <a-menu-item key="analytics">
          <router-link to="/analytics" @click="handleMenuClick">
            <span class="icon">
              <img src="/images/report.png" alt="Reports" width="20" height="20">
            </span>
            <span class="label">Reports</span>
          </router-link>
        </a-menu-item>

        <a-menu-item key="maintenance">
          <router-link to="/maintenance" @click="handleMenuClick">
            <span class="icon">
              <img src="/images/maintenance.png" alt="Maintenance" width="20" height="20">
            </span>
            <span class="label">Maintenance</span>
          </router-link>
        </a-menu-item>

        <a-menu-item key="notifications">
          <router-link to="/notifications" @click="handleMenuClick">
            <span class="icon">
              <img src="/images/notification.png" alt="Notifications" width="20" height="20">
            </span>
            <span class="label">Notifications</span>
          </router-link>
        </a-menu-item>

        <a-menu-item key="history">
          <router-link to="/history" @click="handleMenuClick">
            <span class="icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 2C8.44772 2 8 2.44772 8 3C8 3.55228 8.44772 4 9 4H11C11.5523 4 12 3.55228 12 3C12 2.44772 11.5523 2 11 2H9Z" fill="#737791"/>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M4 5C4 3.89543 4.89543 3 6 3C6 4.65685 7.34315 6 9 6H11C12.6569 6 14 4.65685 14 3C15.1046 3 16 3.89543 16 5V16C16 17.1046 15.1046 18 14 18H6C4.89543 18 4 17.1046 4 16V5ZM7 9C6.44772 9 6 9.44772 6 10C6 10.5523 6.44772 11 7 11H7.01C7.56228 11 8.01 10.5523 8.01 10C8.01 9.44772 7.56228 9 7.01 9H7ZM10 9C9.44772 9 9 9.44772 9 10C9 10.5523 9.44772 11 10 11H13C13.5523 11 14 10.5523 14 10C14 9.44772 13.5523 9 13 9H10ZM7 13C6.44772 13 6 13.4477 6 14C6 14.5523 6.44772 15 7 15H7.01C7.56228 15 8.01 14.5523 8.01 14C8.01 13.4477 7.56228 13 7.01 13H7ZM10 13C9.44772 13 9 13.4477 9 14C9 14.5523 9.44772 15 10 15H13C13.5523 15 14 14.5523 14 14C14 13.4477 13.5523 13 13 13H10Z" fill="#737791"/>
              </svg>
            </span>
            <span class="label">History</span>
          </router-link>
        </a-menu-item>

        <a-menu-item key="header" class="menu-item-header" disabled>
          <span>Account</span>
        </a-menu-item>

        <!-- User Profile Section (when logged in) -->
        <template v-if="isAuthenticated">
          <a-menu-item key="profile" class="user-profile-item">
            <div class="user-profile">
              <div class="user-avatar">
                {{ userInitials }}
              </div>
              <div class="user-info" v-if="!isCollapsed || isMobileView">
                <span class="user-name">{{ userName }}</span>
                <span class="user-role">{{ userRole }}</span>
              </div>
            </div>
          </a-menu-item>

          <a-menu-item key="settings">
            <router-link to="/settings" @click="handleMenuClick">
              <span class="icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M11.4892 3.17094C11.1102 1.60969 8.8898 1.60969 8.51078 3.17094C8.26594 4.17949 7.11045 4.65811 6.22416 4.11809C4.85218 3.28212 3.28212 4.85218 4.11809 6.22416C4.65811 7.11045 4.17949 8.26593 3.17094 8.51078C1.60969 8.8898 1.60969 11.1102 3.17094 11.4892C4.17949 11.7341 4.65811 12.8896 4.11809 13.7758C3.28212 15.1478 4.85218 16.7179 6.22417 15.8819C7.11045 15.3419 8.26594 15.8205 8.51078 16.8291C8.8898 18.3903 11.1102 18.3903 11.4892 16.8291C11.7341 15.8205 12.8896 15.3419 13.7758 15.8819C15.1478 16.7179 16.7179 15.1478 15.8819 13.7758C15.3419 12.8896 15.8205 11.7341 16.8291 11.4892C18.3903 11.1102 18.3903 8.8898 16.8291 8.51078C15.8205 8.26593 15.3419 7.11045 15.8819 6.22416C16.7179 4.85218 15.1478 3.28212 13.7758 4.11809C12.8896 4.65811 11.7341 4.17949 11.4892 3.17094ZM10 13C11.6569 13 13 11.6569 13 10C13 8.34315 11.6569 7 10 7C8.34315 7 7 8.34315 7 10C7 11.6569 8.34315 13 10 13Z" fill="#111827"/>
                </svg>
              </span>
              <span class="label">Settings</span>
            </router-link>
          </a-menu-item>

          <a-menu-item key="logout" class="logout-item">
            <a @click="handleLogout">
              <span class="icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M3 3C2.44772 3 2 3.44772 2 4V16C2 16.5523 2.44772 17 3 17H7C7.55228 17 8 16.5523 8 16C8 15.4477 7.55228 15 7 15H4V5H7C7.55228 5 8 4.55228 8 4C8 3.44772 7.55228 3 7 3H3ZM13.2929 6.29289C13.6834 5.90237 14.3166 5.90237 14.7071 6.29289L17.7071 9.29289C18.0976 9.68342 18.0976 10.3166 17.7071 10.7071L14.7071 13.7071C14.3166 14.0976 13.6834 14.0976 13.2929 13.7071C12.9024 13.3166 12.9024 12.6834 13.2929 12.2929L14.5858 11H7C6.44772 11 6 10.5523 6 10C6 9.44772 6.44772 9 7 9H14.5858L13.2929 7.70711C12.9024 7.31658 12.9024 6.68342 13.2929 6.29289Z" fill="#ef4444"/>
                </svg>
              </span>
              <span class="label" style="color: #ef4444;">Logout</span>
            </a>
          </a-menu-item>
        </template>

        <!-- Guest menu items (when not logged in) -->
        <template v-else>
          <a-menu-item key="login">
            <router-link to="/login" @click="handleMenuClick">
              <span class="icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M3 3C2.44772 3 2 3.44772 2 4V16C2 16.5523 2.44772 17 3 17H7C7.55228 17 8 16.5523 8 16C8 15.4477 7.55228 15 7 15H4V5H7C7.55228 5 8 4.55228 8 4C8 3.44772 7.55228 3 7 3H3ZM13.2929 6.29289C13.6834 5.90237 14.3166 5.90237 14.7071 6.29289L17.7071 9.29289C18.0976 9.68342 18.0976 10.3166 17.7071 10.7071L14.7071 13.7071C14.3166 14.0976 13.6834 14.0976 13.2929 13.7071C12.9024 13.3166 12.9024 12.6834 13.2929 12.2929L14.5858 11H7C6.44772 11 6 10.5523 6 10C6 9.44772 6.44772 9 7 9H14.5858L13.2929 7.70711C12.9024 7.31658 12.9024 6.68342 13.2929 6.29289Z" fill="#111827"/>
                </svg>
              </span>
              <span class="label">Sign In</span>
            </router-link>
          </a-menu-item>

          <a-menu-item key="register">
            <router-link to="/register" @click="handleMenuClick">
              <span class="icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 9C11.6569 9 13 7.65685 13 6C13 4.34315 11.6569 3 10 3C8.34315 3 7 4.34315 7 6C7 7.65685 8.34315 9 10 9Z" fill="#111827"/>
                <path d="M3 18C3 14.134 6.13401 11 10 11C13.866 11 17 14.134 17 18H3Z" fill="#111827"/>
              </svg>
              </span>
              <span class="label">Sign Up</span>
            </router-link>
          </a-menu-item>
        </template>
      </a-menu>
    </div>
    <!-- / Sidebar Navigation Menu -->
  </div>
  <!-- / Main Sidebar -->
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { authService } from '../../services/auth.service';

// Props
const props = defineProps({
  sidebarCollapsed: {
    type: Boolean,
    default: false,
  },
  isMobileView: {
    type: Boolean,
    default: false,
  },
  sidebarColor: {
    type: String,
    default: 'primary',
  },
  sidebarTheme: {
    type: String,
    default: 'light',
  },
});

// Emits
const emit = defineEmits(['toggleSidebar']);

// Reactive state
const isCollapsed = ref(props.sidebarCollapsed);
const route = useRoute();

// Compute selected menu keys based on current route
const selectedKeys = computed(() => {
  const path = route.path;
  if (path.includes('/dashboard')) return ['dashboard'];
  if (path.includes('/air-conditioning-unit')) return ['air-conditioning-unit'];
  if (path.includes('/location')) return ['location'];
  if (path.includes('/analytics')) return ['analytics'];
  if (path.includes('/maintenance')) return ['maintenance'];
  if (path.includes('/notifications')) return ['notifications'];
  if (path.includes('/history')) return ['history'];
  if (path.includes('/settings')) return ['settings'];
  if (path.includes('/sign-in')) return ['sign-in'];
  if (path.includes('/sign-up')) return ['sign-up'];
  return ['dashboard'];
});

// Watch for prop changes
watch(() => props.sidebarCollapsed, (newVal) => {
  isCollapsed.value = newVal;
});

// Handle collapse event
const handleCollapse = (collapsed) => {
  isCollapsed.value = collapsed;
  emit('toggleSidebar', collapsed);
};

// Handle menu item click (close sidebar on mobile)
const handleMenuClick = () => {
  if (props.isMobileView) {
    handleCollapse(true);
  }
};

// Auth state
const router = useRouter();
const isAuthenticated = computed(() => authService.isAuthenticated());
const currentUser = computed(() => authService.getUser());

const userName = computed(() => currentUser.value?.name || 'User');
const userRole = computed(() => {
  const role = currentUser.value?.role || 'user';
  return role.charAt(0).toUpperCase() + role.slice(1);
});
const userInitials = computed(() => {
  const name = userName.value;
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
});

const isAdmin = computed(() => {
  const role = currentUser.value?.role?.toLowerCase();
  return role === 'admin';
});

// Logout handler
const handleLogout = () => {
  authService.logout();
  message.success('Logged out successfully');
  router.push('/login');
  if (props.isMobileView) {
    handleCollapse(true);
  }
};
</script>

<style scoped lang="scss">
.sider-primary {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 250px;
  min-height: 100vh;
  background: white;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
  z-index: 1002;
  transition: transform 0.3s cubic-bezier(0.2, 0, 0, 1);
  
  // Mobile specific styles
  &.sidebar-mobile-open {
    transform: translateX(0);
    animation: slideIn 0.3s ease;
  }
  
  @keyframes slideIn {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }
  
  .sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    min-height: 72px;
    
    .brand {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
      
      img {
        height: 32px;
        width: auto;
        min-width: 32px;
      }
      
      span {
        font-size: 18px;
        font-weight: 600;
        color: #111827;
        white-space: nowrap;
        transition: opacity 0.3s;
      }
    }
    
    .sidebar-close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      color: #6b7280;
      cursor: pointer;
      border-radius: 6px;
      transition: all 0.2s;
      flex-shrink: 0;
      
      &:hover {
        background: #f3f4f6;
        color: #111827;
      }
      
      &:active {
        transform: scale(0.95);
      }
    }
  }
  
  hr {
    margin: 0 24px 16px;
    border: none;
    border-top: 1px solid #e5e7eb;
  }
  
  .sidebar-menu-wrapper {
    overflow-y: auto;
    overflow-x: hidden;
    max-height: calc(100vh - 180px);
    padding-bottom: 20px;

    scrollbar-width: none;
    -ms-overflow-style: none;
    
    // Custom scrollbar
    &::-webkit-scrollbar {
      display: none;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #e5e7eb;
      border-radius: 3px;
      
      &:hover {
        background: #d1d5db;
      }
    }
  }
  
  :deep(.ant-menu) {
    border: none;
    background: transparent;
    
    .ant-menu-item {
      margin: 4px 16px;
      height: 40px;
      line-height: 40px;
      border-radius: 8px;
      padding-left: 16px !important;
      transition: all 0.2s;
      
      // User profile item styles
      &.user-profile-item {
        height: auto;
        line-height: normal;
        padding: 12px 16px !important;
        margin-top: 8px;
        margin-bottom: 8px;
        background: #f8fafc;
        cursor: default;
        
        &:hover {
          background: #f1f5f9;
        }
        
        .user-profile {
          display: flex;
          align-items: center;
          gap: 12px;
          
          .user-avatar {
            width: 36px;
            height: 36px;
            min-width: 36px;
            background: linear-gradient(135deg, #6366f1, #8b5cf6);
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 13px;
            font-weight: 600;
          }
          
          .user-info {
            display: flex;
            flex-direction: column;
            overflow: hidden;
            
            .user-name {
              font-size: 14px;
              font-weight: 600;
              color: #1e293b;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            
            .user-role {
              font-size: 12px;
              color: #64748b;
            }
          }
        }
      }
      
      // Logout item styles
      &.logout-item {
        a {
          color: #ef4444 !important;
          
          &:hover {
            color: #dc2626 !important;
          }
        }
      }
      
      &:hover {
        background-color: #f3f4f6;
      }
      
      a {
        display: flex;
        align-items: center;
        gap: 12px;
        color: #6b7280;
        text-decoration: none;
        transition: all 0.2s;
        
        &:hover {
          color: #a9c0f1;
        }
        
        .icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          flex-shrink: 0;
          
          // Default PNG filter
          img {
            filter: invert(47%) sepia(8%) saturate(935%) hue-rotate(201deg) brightness(95%) contrast(88%);
            transition: filter 0.3s ease;
          }
          
          svg {
            width: 20px;
            height: 20px;
          }
        }
        
        .label {
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
    
    .menu-item-header {
      margin-top: 16px;
      margin-bottom: 4px;
      height: auto;
      line-height: normal;
      padding: 8px 16px;
      cursor: default;
      pointer-events: none;
      
      span {
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #9ca3af;
      }
      
      &:hover {
        background: transparent !important;
      }
    }
  }
  
  .sidebar-footer {
    position: absolute;
    bottom: 24px;
    left: 0;
    right: 0;
    padding: 0 24px;
    display: flex;
    justify-content: center;
  }
}

// Theme variations
.ant-layout-sider-dark {
  background: #1f2937 !important;
  
  .sidebar-header {
    .brand span {
      color: white;
    }
    
    .sidebar-close {
      color: #9ca3af;
      
      &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
      }
    }
  }
  
  :deep(.ant-menu-item a) {
    color: #d1d5db;
    
    &:hover {
      color: white;
    }
  }
  
  :deep(.ant-menu-item) {
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }
  
  hr {
    border-color: rgba(255, 255, 255, 0.1);
  }
}

// Responsive Breakpoints
@media (max-width: 991px) {
  .sider-primary {
    // Hide sidebar by default on mobile/tablet
    transform: translateX(-100%);
    
    &.sidebar-mobile-open {
      transform: translateX(0);
    }
    
    .sidebar-header {
      padding: 16px 20px;
    }
    
    hr {
      margin: 0 20px 12px;
    }
    
    .sidebar-menu-wrapper {
      max-height: calc(100vh - 100px);
    }
  }
}

@media (max-width: 768px) {
  .sider-primary {
    .sidebar-header {
      .brand {
        img {
          height: 28px;
        }
        
        span {
          font-size: 16px;
        }
      }
    }
    
    :deep(.ant-menu-item) {
      margin: 4px 12px;
      height: 44px;
      line-height: 44px;
      
      a .label {
        font-size: 15px;
      }
    }
  }
}

@media (max-width: 576px) {
  .sider-primary {
    .sidebar-header {
      padding: 14px 16px;
    }
    
    :deep(.ant-menu-item) {
      margin: 3px 10px;
      
      a .label {
        font-size: 14px;
      }
    }
  }
}
</style>